// A scorecard cell's history (KPIS-MODE, founder-approved 2026-10-22): a popover anchored to the
// cell listing every observation of that metric, subject and period, newest first — the value,
// when, who entered it, how (by hand, import, events, an agent), the reason a correction gave,
// and whether a later row replaced it or it was deleted; an agent's row carries its provenance
// line and evidence link (the `AgentReportMark` text). One `GET observations` (reader access):
// a plain member gets a 403 there, which reads "History is visible to managers", not an error.
// Opened from the cell's clock icon, its context menu, or `H` on a focused cell.
import { ApiError } from "@companyos/sdk";
import { Badge, Popover, PopoverContent, PopoverHeader, PopoverTitle } from "@companyos/ui";
import { queryOptions, useQuery } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import type { MetricDef } from "../../api/types";
import { formatDateTime, formatMetricValue } from "../format";
import { Loading } from "../screen-parts";
import { ErrorNotice } from "../structure/parts";
import { type Grain, type HistoryState, historyRows } from "./scorecard-model";

export type HistoryTarget = {
  metric: string;
  metricName: string;
  unit: MetricDef["unit"];
  subject: { kind: "seat" | "team" | "company"; id: string };
  grain: Grain;
  period: string;
  /** "Placements · W42" */
  title: string;
};

export const historyKey = (co: string, t?: HistoryTarget) =>
  t
    ? (["company", co, "observations", "cell", t.metric, t.subject.id, t.grain, t.period] as const)
    : (["company", co, "observations"] as const);

const historyQuery = (api: Api, co: string, t: HistoryTarget) =>
  queryOptions({
    queryKey: historyKey(co, t),
    queryFn: () =>
      api.client.observations.list({
        params: { co },
        query: {
          metric: t.metric,
          subject_kind: t.subject.kind,
          subject_id: t.subject.id,
          period_type: t.grain,
          from: t.period,
          to: t.period,
          limit: 50,
        },
      }),
    retry: false,
  });

const STATE_LABEL: Record<HistoryState, string> = {
  live: "Current",
  superseded: "Replaced",
  deleted: "Deleted",
};

export function CellHistoryPopover({
  api,
  co,
  currency,
  target,
  anchor,
  names,
  onClose,
}: {
  api: Api;
  co: string;
  currency: string;
  target: HistoryTarget;
  anchor: Element | null;
  /** Membership id → display name (the org chart's holders). */
  names: ReadonlyMap<string, string>;
  onClose: () => void;
}) {
  const q = useQuery(historyQuery(api, co, target));
  const forbidden = q.error instanceof ApiError && q.error.status === 403;
  const rows = q.data ? historyRows(q.data.data, names) : [];
  return (
    <Popover open onOpenChange={(open) => (open ? null : onClose())}>
      <PopoverContent
        anchor={anchor}
        align="start"
        className="w-80 gap-2 p-3"
        data-slot="cell-history"
      >
        <PopoverHeader>
          <PopoverTitle>History · {target.title}</PopoverTitle>
        </PopoverHeader>
        {forbidden ? (
          <p className="text-muted-foreground">History is visible to managers.</p>
        ) : q.isError ? (
          <ErrorNotice what="The history" error={q.error} />
        ) : q.isPending ? (
          <Loading label="Loading the history" rows={2} />
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground">No number recorded for this period.</p>
        ) : (
          <ol className="flex max-h-72 flex-col divide-y divide-border overflow-y-auto">
            {rows.map((r) => (
              <li
                key={r.id}
                data-observation={r.id}
                data-state={r.state}
                className="flex flex-col gap-0.5 py-2 first:pt-0 last:pb-0"
              >
                <span className="flex items-center gap-2">
                  <span
                    className={`font-medium tabular-nums ${r.state === "live" ? "text-foreground" : "text-muted-foreground line-through"}`}
                  >
                    {r.value === null ? "—" : formatMetricValue(r.value, target.unit, currency)}
                  </span>
                  <Badge variant={r.state === "live" ? "success" : "neutral"}>
                    {STATE_LABEL[r.state]}
                  </Badge>
                  <span className="ml-auto text-xs text-muted-foreground">{r.source}</span>
                </span>
                <span className="text-xs text-muted-foreground">
                  {r.who} · {formatDateTime(r.at)}
                </span>
                {r.note ? (
                  <span className="text-xs text-foreground" data-field="note">
                    “{r.note}”
                  </span>
                ) : null}
                {r.provenance ? (
                  <span className="text-xs text-muted-foreground" data-slot="agent-report-line">
                    {r.provenance}
                    {r.evidenceUrl ? (
                      <>
                        {" "}
                        <a
                          className="text-primary underline-offset-4 hover:underline"
                          href={r.evidenceUrl}
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          Evidence
                        </a>
                      </>
                    ) : null}
                  </span>
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </PopoverContent>
    </Popover>
  );
}
